import React, { useEffect } from "react";
import styled from "styled-components";
import { useTranslation, Trans } from "react-i18next";
import { HashLink } from "react-router-hash-link";
import {
  DesktopIcon,
  RocketIcon,
  LockIcon,
  UsersIcon,
  CheckCircleIcon,
  ClockIcon,
  ToolsIcon,
  CodeIcon,
  DatabaseIcon,
  CloudIcon,
} from "./IconComponents";
import SEO from "./SEO";

const PageContainer = styled.div`
  min-height: 100vh;
  padding: 2rem 0;
  background-color: var(--light-background);
`;

const HeroSection = styled.section`
  background: var(--primary-color);
  color: white;
  padding: 6rem 0 4rem;
  text-align: center;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  opacity: 0.9;
  max-width: 700px;
  margin: 0 auto 2rem;
  line-height: 1.6;
`;

const ContentSection = styled.section`
  padding: 4rem 0;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  text-align: center;
  margin-top: 4rem;
  margin-bottom: 2rem;
  color: var(--text-color);

  span {
    color: var(--primary-color);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const Card = styled.div`
  background: var(--card-background);
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }
`;

const IconWrapper = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: var(--primary-color);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  color: white;
  font-size: 1.5rem;
`;

const CardTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const CardDescription = styled.p`
  color: var(--text-color);
  line-height: 1.6;
`;

const ProcessSection = styled.section`
  background: var(--card-background);
  padding: 4rem 0;
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const ProcessCard = styled.div`
  text-align: center;
  padding: 2rem;
`;

const ProcessNumber = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: var(--primary-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0 auto 1.5rem;
`;

const ProcessTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const ProcessDescription = styled.p`
  color: var(--text-color);
  line-height: 1.6;
  font-size: 0.95rem;
`;

const BenefitsSection = styled.section`
  padding: 4rem 0;
  background-color: var(--light-background);
`;

const BenefitsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin: 3rem 0;
`;

const BenefitItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem;
  background: var(--card-background);
  border-radius: 8px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.05);
`;

const BenefitIcon = styled.div`
  color: var(--primary-color);
  font-size: 1.5rem;
  margin-top: 0.2rem;
  flex-shrink: 0;
`;

const BenefitText = styled.div`
  h5 {
    font-size: 1.1rem;
    margin-bottom: 0.5rem;
    color: var(--text-color);
  }

  p {
    color: var(--text-color);
    line-height: 1.5;
    font-size: 0.95rem;
  }
`;

const TechnologySection = styled.section`
  background: var(--card-background);
  padding: 4rem 0;
`;

const TechnologyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const TechCard = styled.div`
  text-align: center;
  padding: 1.5rem;
  border-radius: 8px;
  background: var(--light-background);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  }
`;

const TechIcon = styled.div`
  color: var(--primary-color);
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const TechName = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: var(--text-color);
`;

const TechDescription = styled.p`
  color: var(--text-color);
  font-size: 0.9rem;
  line-height: 1.4;
`;

const CaseStudySection = styled.section`
  padding: 4rem 0;
  background-color: var(--light-background);
`;

const CaseStudyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const CaseStudyCard = styled.div`
  background: var(--card-background);
  border-radius: 12px;
  padding: 2.5rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(
      90deg,
      var(--primary-color),
      var(--secondary-color)
    );
  }
`;

const CaseStudyTitle = styled.h4`
  font-size: 1.3rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const CaseStudyText = styled.p`
  color: var(--text-color);
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const CaseStudyResult = styled.div`
  background: var(--light-background);
  padding: 1rem;
  border-radius: 6px;
  border-left: 4px solid var(--primary-color);

  h5 {
    color: var(--primary-color);
    margin-bottom: 0.5rem;
    font-size: 1rem;
  }

  p {
    color: var(--text-color);
    font-size: 0.9rem;
    margin: 0;
  }
`;

const ServiceTypesSection = styled.section`
  background: var(--card-background);
  padding: 4rem 0;
`;

const ServiceTypeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const ServiceTypeCard = styled.div`
  background: var(--light-background);
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  position: relative;
  border: 2px solid transparent;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--primary-color);
    transform: translateY(-5px);
  }
`;

const ServiceTypeTitle = styled.h4`
  font-size: 1.4rem;
  margin-bottom: 1rem;
  color: var(--text-color);
`;

const ServiceTypeDescription = styled.p`
  color: var(--text-color);
  margin-bottom: 1.5rem;
  font-size: 0.95rem;
  line-height: 1.5;
`;

const ServiceTypeFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;

  li {
    color: var(--text-color);
    padding: 0.5rem 0;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &:before {
      content: "✓";
      color: var(--primary-color);
      font-weight: bold;
    }
  }
`;

const FAQSection = styled.section`
  padding: 4rem 0;
  background-color: var(--light-background);
`;

const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const FAQItem = styled.div`
  background: var(--card-background);
  margin-bottom: 1rem;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const FAQQuestion = styled.h4`
  padding: 1.5rem;
  margin: 0;
  background: var(--primary-color);
  color: white;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background: var(--secondary-color);
  }
`;

const FAQAnswer = styled.p`
  padding: 1.5rem;
  margin: 0;
  color: var(--text-color);
  line-height: 1.6;
`;

const CTASection = styled.section`
  background: var(--primary-color);
  color: white;
  padding: 4rem 0;
  text-align: center;
`;

const CTATitle = styled.h3`
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const CTADescription = styled.p`
  font-size: 1.1rem;
  margin-bottom: 2rem;
  opacity: 0.9;
`;

const CTAButton = styled(HashLink)`
  display: block;
  width: 50%;
  margin: 2rem auto 0;
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 4px;
  background-color: var(--primary-color);
  color: var(--light-text-color);
  font-size: 1.05rem;
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background-color: var(--secondary-color);
    transform: translateY(-2px);
  }

  &:focus-visible {
    outline: 3px solid var(--primary-color);
    outline-offset: 4px;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 3rem;
  color: var(--text-color);
`;

const Desktopanwendungen: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const advantages = [
    {
      icon: <RocketIcon />,
      title: "Hohe Performance",
      description:
        "Desktop-Anwendungen nutzen die volle Rechenleistung des lokalen Systems und bieten dadurch besonders schnelle Reaktionszeiten, auch bei komplexen Berechnungen und großen Datenmengen.",
    },
    {
      icon: <LockIcon />,
      title: "Maximale Datensicherheit",
      description:
        "Sensible Unternehmensdaten können lokal oder in Ihrer eigenen Infrastruktur verarbeitet werden, ohne zwingend auf externe Cloud-Dienste angewiesen zu sein.",
    },
    {
      icon: <UsersIcon />,
      title: "Maßgeschneiderte Bedienoberflächen",
      description:
        "Wir entwickeln intuitive, auf Ihre Arbeitsabläufe zugeschnittene Oberflächen, die die Produktivität Ihrer Mitarbeiter spürbar steigern.",
    },
    {
      icon: <ToolsIcon />,
      title: "Tiefe Systemintegration",
      description:
        "Desktop-Software kann nahtlos mit Hardware, lokalen Datenbanken und bestehenden Systemen wie ERP oder Dynamics 365 integriert werden.",
    },
  ];

  const process = [
    {
      title: "Anforderungsanalyse",
      description:
        "Wir analysieren Ihre Geschäftsprozesse und definieren gemeinsam die funktionalen sowie technischen Anforderungen an Ihre Desktop-Anwendung.",
    },
    {
      title: "Konzeption & UI/UX-Design",
      description:
        "Entwicklung eines durchdachten Bedienkonzepts und benutzerfreundlicher Oberflächen, die auf Ihre Zielgruppe abgestimmt sind.",
    },
    {
      title: "Entwicklung & Integration",
      description:
        "Programmierung der Anwendung mit modernen Technologien sowie Anbindung an bestehende Systeme, Datenbanken und Hardware.",
    },
    {
      title: "Testing, Rollout & Support",
      description:
        "Umfassende Qualitätssicherung, professionelle Einführung im Unternehmen sowie kontinuierlicher Support und Weiterentwicklung.",
    },
  ];

  const benefits = [
    {
      title: "Offline-Fähigkeit",
      description:
        "Desktop-Anwendungen funktionieren auch ohne durchgehende Internetverbindung zuverlässig und stabil.",
    },
    {
      title: "Optimierte Ressourcennutzung",
      description:
        "Direkter Zugriff auf Systemressourcen ermöglicht performante Verarbeitung auch rechenintensiver Aufgaben.",
    },
    {
      title: "Individuelle Erweiterbarkeit",
      description:
        "Wir entwickeln modulare Architekturen, die sich flexibel an wachsende Anforderungen anpassen lassen.",
    },
    {
      title: "Langfristige Stabilität",
      description:
        "Robuste, gut getestete Anwendungen sorgen für einen stabilen und wartungsarmen Betrieb im Unternehmensalltag.",
    },
    {
      title: "Plattformübergreifende Verfügbarkeit",
      description:
        "Auf Wunsch entwickeln wir Ihre Anwendung für Windows, macOS und Linux, um alle Arbeitsplätze abzudecken.",
    },
    {
      title: "Nahtlose Hardware-Anbindung",
      description:
        "Direkte Anbindung an Peripheriegeräte wie Drucker, Scanner oder Messgeräte für spezialisierte Arbeitsabläufe.",
    },
  ];

  const technologies = [
    {
      icon: <CodeIcon />,
      name: ".NET / C#",
      description: "Robuste Windows-Anwendungen mit moderner .NET-Technologie",
    },
    {
      icon: <DesktopIcon />,
      name: "Electron",
      description: "Plattformübergreifende Desktop-Apps mit Web-Technologien",
    },
    {
      icon: <ToolsIcon />,
      name: "WPF & WinForms",
      description: "Klassische und moderne Windows-Benutzeroberflächen",
    },
    {
      icon: <DatabaseIcon />,
      name: "SQL & lokale DBs",
      description: "Effiziente Datenhaltung direkt am Arbeitsplatz",
    },
    {
      icon: <CloudIcon />,
      name: "Hybrid-Lösungen",
      description: "Kombination aus lokaler App und Cloud-Synchronisation",
    },
  ];

  const caseStudies = [
    {
      title: "Warenwirtschaftssystem für den Einzelhandel",
      description:
        "Entwicklung einer individuellen Desktop-Anwendung zur Verwaltung von Lagerbeständen, Bestellungen und Kassensystemen für ein mittelständisches Handelsunternehmen.",
      result: {
        title: "Ergebnisse:",
        description:
          "30% schnellere Bestandsverwaltung, reduzierte Fehlerquote bei Bestellungen, nahtlose Anbindung an bestehende Kassenhardware",
      },
    },
    {
      title: "Produktionsplanungs-Tool für die Fertigung",
      description:
        "Maßgeschneiderte Desktop-Software zur Planung und Steuerung von Produktionsabläufen mit direkter Anbindung an Maschinensteuerungen.",
      result: {
        title: "Ergebnisse:",
        description:
          "20% höhere Maschinenauslastung, verbesserte Planungssicherheit, Echtzeit-Überblick über alle Produktionslinien",
      },
    },
  ];

  const serviceTypes = [
    {
      title: "Individuelle Desktop-Software",
      description:
        "Komplett maßgeschneiderte Anwendungen, die exakt auf Ihre Geschäftsprozesse und Anforderungen zugeschnitten sind.",
      features: [
        "Individuelle Funktionsentwicklung",
        "Benutzerfreundliches UI/UX-Design",
        "Anbindung an bestehende Systeme",
        "Skalierbare Architektur",
        "Langfristiger Support",
      ],
    },
    {
      title: "Modernisierung bestehender Anwendungen",
      description:
        "Wir überarbeiten und modernisieren veraltete Desktop-Anwendungen, um Performance, Sicherheit und Benutzerfreundlichkeit zu verbessern.",
      features: [
        "Technologie-Migration",
        "UI-Modernisierung",
        "Performance-Optimierung",
        "Sicherheitsupdates",
        "Erweiterung um neue Funktionen",
      ],
    },
    {
      title: "Systemintegration & Hardware-Anbindung",
      description:
        "Anbindung Ihrer Desktop-Anwendung an Peripheriegeräte, Maschinen, Datenbanken und weitere Unternehmenssysteme.",
      features: [
        "Schnittstellenentwicklung (APIs)",
        "Hardware-Kommunikation",
        "Datenbankanbindung",
        "ERP-/Dynamics 365-Integration",
        "Automatisierte Datenflüsse",
      ],
    },
  ];

  const faqs = [
    {
      question: "Warum eine Desktop-Anwendung statt einer Webanwendung?",
      answer:
        "Desktop-Anwendungen bieten höhere Performance, funktionieren offline und ermöglichen tiefen Zugriff auf lokale Hardware und Systemressourcen – ideal für rechenintensive oder sicherheitskritische Anwendungsfälle.",
    },
    {
      question: "Für welche Betriebssysteme entwickeln Sie?",
      answer:
        "Wir entwickeln Anwendungen für Windows, macOS und Linux und setzen dabei je nach Anforderung auf native oder plattformübergreifende Technologien.",
    },
    {
      question: "Können Sie bestehende Desktop-Anwendungen modernisieren?",
      answer:
        "Ja, wir übernehmen die Analyse, Überarbeitung und Modernisierung bestehender Anwendungen, um diese performanter, sicherer und benutzerfreundlicher zu gestalten.",
    },
    {
      question:
        "Lässt sich die Anwendung mit unserer bestehenden Software verbinden?",
      answer:
        "Ja, wir integrieren Ihre Desktop-Anwendung nahtlos in bestehende Systeme wie ERP-Lösungen, Datenbanken, Dynamics 365 oder spezielle Hardware.",
    },
    {
      question: "Wie sieht es mit Wartung und Updates nach dem Launch aus?",
      answer:
        "Wir bieten langfristigen Support inklusive Wartung, Updates und Weiterentwicklung, damit Ihre Anwendung stets aktuell und zuverlässig bleibt.",
    },
  ];

  const SectionTitleQuestion = styled.h2`
    font-size: 2.5rem;
    text-align: center;
    margin-top: 0rem;
    margin-bottom: 2rem;
    color: var(--text-color);

    span {
      color: var(--primary-color);
    }
  `;

  const CTAContainer = styled.div`
    text-align: center;
    margin-top: 4rem;
    margin-bottom: 2rem;
    padding: 3rem;
    background-color: var(--card-background);
    border-radius: 8px;
  `;

  return (
    <PageContainer>
      <SEO
        title="Desktop-Anwendungen entwickeln lassen"
        description="Maßgeschneiderte Desktop-Software für Windows, macOS und Linux: schneller, sicherer und nahtlos in Ihre IT-Landschaft integriert. Byte Forge entwickelt Ihre Lösung."
        path="/desktop-anwendungen"
        keywords="Desktop-Anwendungen, Desktop-Software entwickeln, Windows Anwendung, Software Entwicklung Desktop"
      />
      <Container>
        <SectionTitle>
          <Trans>
            Leistungsstarke Desktop-Anwendungen für <span>Ihr Unternehmen</span>
          </Trans>
        </SectionTitle>
        <SectionSubtitle>
          Wir entwickeln maßgeschneiderte Desktop-Software, die Ihre internen
          Prozesse beschleunigt, die Produktivität steigert und sich nahtlos in
          Ihre bestehende IT-Landschaft integriert.
        </SectionSubtitle>
      </Container>
      <ContentSection>
        <Container>
          <SectionTitleQuestion>
            Warum <span>Desktop-Anwendungen</span>?
          </SectionTitleQuestion>
          <Grid>
            {advantages.map((advantage, index) => (
              <Card key={index}>
                <IconWrapper>{advantage.icon}</IconWrapper>
                <CardTitle>{advantage.title}</CardTitle>
                <CardDescription>{advantage.description}</CardDescription>
              </Card>
            ))}
          </Grid>
        </Container>
      </ContentSection>

      <ProcessSection>
        <Container>
          <SectionTitle>
            Unser <span>Entwicklungsprozess</span>
          </SectionTitle>
          <ProcessGrid>
            {process.map((step, index) => (
              <ProcessCard key={index}>
                <ProcessNumber>{index + 1}</ProcessNumber>
                <ProcessTitle>{step.title}</ProcessTitle>
                <ProcessDescription>{step.description}</ProcessDescription>
              </ProcessCard>
            ))}
          </ProcessGrid>
        </Container>
      </ProcessSection>

      <BenefitsSection>
        <Container>
          <SectionTitle>
            Ihre <span>Vorteile</span>
          </SectionTitle>
          <BenefitsList>
            {benefits.map((benefit, index) => (
              <BenefitItem key={index}>
                <BenefitIcon>
                  <CheckCircleIcon />
                </BenefitIcon>
                <BenefitText>
                  <h5>{benefit.title}</h5>
                  <p>{benefit.description}</p>
                </BenefitText>
              </BenefitItem>
            ))}
          </BenefitsList>
        </Container>
      </BenefitsSection>

      <TechnologySection>
        <Container>
          <SectionTitle>
            Unsere <span>Technologien</span>
          </SectionTitle>
          <TechnologyGrid>
            {technologies.map((tech, index) => (
              <TechCard key={index}>
                <TechIcon>{tech.icon}</TechIcon>
                <TechName>{tech.name}</TechName>
                <TechDescription>{tech.description}</TechDescription>
              </TechCard>
            ))}
          </TechnologyGrid>
        </Container>
      </TechnologySection>

      <CaseStudySection>
        <Container>
          <SectionTitle>
            Erfolgreiche <span>Projekte</span>
          </SectionTitle>
          <CaseStudyGrid>
            {caseStudies.map((caseStudy, index) => (
              <CaseStudyCard key={index}>
                <CaseStudyTitle>{caseStudy.title}</CaseStudyTitle>
                <CaseStudyText>{caseStudy.description}</CaseStudyText>
                <CaseStudyResult>
                  <h5>{caseStudy.result.title}</h5>
                  <p>{caseStudy.result.description}</p>
                </CaseStudyResult>
              </CaseStudyCard>
            ))}
          </CaseStudyGrid>
        </Container>
      </CaseStudySection>

      <ServiceTypesSection>
        <Container>
          <SectionTitle>
            Unsere <span>Leistungen</span>
          </SectionTitle>
          <ServiceTypeGrid>
            {serviceTypes.map((service, index) => (
              <ServiceTypeCard key={index}>
                <ServiceTypeTitle>{service.title}</ServiceTypeTitle>
                <ServiceTypeDescription>
                  {service.description}
                </ServiceTypeDescription>
                <ServiceTypeFeatures>
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex}>{feature}</li>
                  ))}
                </ServiceTypeFeatures>
              </ServiceTypeCard>
            ))}
          </ServiceTypeGrid>
        </Container>
      </ServiceTypesSection>

      <FAQSection>
        <Container>
          <SectionTitle>
            Häufig gestellte <span>Fragen</span>
          </SectionTitle>
          <FAQContainer>
            {faqs.map((faq, index) => (
              <FAQItem key={index}>
                <FAQQuestion>{faq.question}</FAQQuestion>
                <FAQAnswer>{faq.answer}</FAQAnswer>
              </FAQItem>
            ))}
          </FAQContainer>
        </Container>
      </FAQSection>
      <Container>
        <CTAContainer>
          <SectionTitleQuestion>
            <Trans>
              Bereit für Ihre <span>individuelle Desktop-Anwendung?</span>
            </Trans>
          </SectionTitleQuestion>
          <SectionSubtitle>
            Lassen Sie uns gemeinsam die passende Softwarelösung für Ihre
            internen Prozesse entwickeln.
            <CTAButton smooth to="/#contact">
              Kostenloses Erstgespräch
            </CTAButton>
          </SectionSubtitle>
        </CTAContainer>
      </Container>
    </PageContainer>
  );
};

export default Desktopanwendungen;
